import { useEffect, useState } from 'react';
import { Modal, Spin, Empty, Tag, App, Progress } from 'antd';
import { useNavigate } from 'react-router-dom';
import { taskService, type KpiTaskBreakdown } from '@/services/taskService';

/**
 * V1.0.30: Modal hiển thị breakdown KPI của 1 task — cây root + descendants
 * với plan/actual per-node, kèm tổng aggregated + % tiến độ.
 *
 * Click vào row task → navigate sang detail của task đó.
 */
export function KpiBreakdownModal({
  taskId,
  rootKpiPlan,
  aggregatedActual,
  onClose,
}: {
  taskId: string;
  rootKpiPlan: number;
  aggregatedActual: number;
  onClose: () => void;
}) {
  const navigate = useNavigate();
  const { message } = App.useApp();
  const [rows, setRows] = useState<KpiTaskBreakdown[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        const data = await taskService.getKpiBreakdown(taskId);
        setRows(data);
      } catch (e: any) {
        message.error('Không tải được breakdown: ' + (e?.response?.data?.message ?? e.message));
      } finally {
        setLoading(false);
      }
    })();
  }, [taskId]);

  const progressPct = rootKpiPlan > 0
    ? Math.min(100, Math.round((aggregatedActual / rootKpiPlan) * 1000) / 10)
    : 0;
  const strokeColor = progressPct >= 100 ? '#52c41a'
    : progressPct >= 70 ? '#1976D2'
    : progressPct >= 40 ? '#fa8c16'
    : '#cf1322';

  // Tính tổng plan của tất cả task con (level >= 1)
  const totalSubPlan = rows.filter(r => r.depth >= 1)
    .reduce((sum, r) => sum + (r.kpiRevenuePlan ?? 0), 0);
  const totalSubActual = rows.filter(r => r.depth >= 1)
    .reduce((sum, r) => sum + (r.kpiRevenueActual ?? 0), 0);

  return (
    <Modal
      title="💰 Báo cáo KPI Doanh thu — Cây task"
      open
      onCancel={onClose}
      footer={null}
      width={820}
    >
      {/* Summary header */}
      <div style={{
        background: '#fffbf0', borderRadius: 6, padding: 12,
        border: '1px solid #ffe7ba', marginBottom: 16,
      }}>
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12,
          marginBottom: 10, fontSize: 12,
        }}>
          <div>
            <div style={{ color: '#7d8a98', fontSize: 11 }}>KPI Kế hoạch (root)</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#1a1e23' }}>
              {rootKpiPlan.toLocaleString('vi-VN')} đ
            </div>
          </div>
          <div>
            <div style={{ color: '#7d8a98', fontSize: 11 }}>Tổng đã đạt (aggregated)</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: '#16a34a' }}>
              {aggregatedActual.toLocaleString('vi-VN')} đ
            </div>
          </div>
          <div>
            <div style={{ color: '#7d8a98', fontSize: 11 }}>% Hoàn thành</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: strokeColor }}>
              {progressPct.toFixed(1)}%
            </div>
          </div>
        </div>
        <Progress percent={progressPct} strokeColor={strokeColor} showInfo={false} />
        <div style={{ fontSize: 11, color: '#5b6875', marginTop: 8 }}>
          Tổng KPI plan task con: <b>{totalSubPlan.toLocaleString('vi-VN')} đ</b>
          {' · '}Tổng actual task con: <b>{totalSubActual.toLocaleString('vi-VN')} đ</b>
        </div>
      </div>

      {/* Tree breakdown */}
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}><Spin /></div>
      ) : rows.length === 0 ? (
        <Empty description="Không có dữ liệu" />
      ) : (
        <div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '2.5fr 0.8fr 1fr 1fr 0.5fr',
            gap: 8,
            padding: '8px 10px',
            fontSize: 11, fontWeight: 700, color: '#48535e',
            background: '#f5f7fa', borderRadius: 4,
            marginBottom: 4,
          }}>
            <div>Mã & Tên task</div>
            <div>Trạng thái</div>
            <div style={{ textAlign: 'right' }}>KPI Plan</div>
            <div style={{ textAlign: 'right' }}>KPI Actual</div>
            <div>Người thực hiện</div>
          </div>
          {rows.map(r => {
            const indent = r.depth * 20;
            const isRoot = r.depth === 0;
            return (
              <div
                key={r.id}
                onClick={() => { navigate(`/tasks/${r.id}`); onClose(); }}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '2.5fr 0.8fr 1fr 1fr 0.5fr',
                  gap: 8,
                  padding: '8px 10px',
                  fontSize: 12,
                  borderBottom: '1px solid #f0f2f5',
                  cursor: 'pointer',
                  background: isRoot ? '#fffbf0' : '#fff',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = '#f5f7fa'; }}
                onMouseLeave={e => { e.currentTarget.style.background = isRoot ? '#fffbf0' : '#fff'; }}
              >
                <div style={{ display: 'flex', alignItems: 'center' }}>
                  <span style={{ width: indent, flexShrink: 0 }} />
                  {r.depth > 0 && (
                    <span style={{ color: '#bdc4cd', marginRight: 6 }}>└─</span>
                  )}
                  <div style={{ minWidth: 0 }}>
                    <div style={{
                      fontSize: 11, color: '#1976D2', fontWeight: 600,
                      fontFamily: 'monospace',
                    }}>
                      {r.code}
                      {isRoot && (
                        <Tag color="orange" style={{ marginLeft: 6, fontSize: 9, padding: '0 4px', lineHeight: '14px' }}>
                          ROOT
                        </Tag>
                      )}
                    </div>
                    <div style={{
                      fontWeight: isRoot ? 600 : 500,
                      color: '#1a1e23',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      {r.title}
                    </div>
                  </div>
                </div>
                <div style={{ alignSelf: 'center' }}>
                  <Tag style={{ fontSize: 10, padding: '0 6px', lineHeight: '18px', margin: 0 }}>
                    {r.status}
                  </Tag>
                </div>
                <div style={{ textAlign: 'right', alignSelf: 'center', color: '#1a1e23', fontWeight: 600 }}>
                  {r.kpiRevenuePlan != null ? `${r.kpiRevenuePlan.toLocaleString('vi-VN')} đ` : <span style={{ color: '#bdc4cd' }}>—</span>}
                </div>
                <div style={{ textAlign: 'right', alignSelf: 'center', color: r.kpiRevenueActual != null ? '#16a34a' : '#bdc4cd', fontWeight: 600 }}>
                  {r.kpiRevenueActual != null ? `${r.kpiRevenueActual.toLocaleString('vi-VN')} đ` : '—'}
                </div>
                <div style={{ alignSelf: 'center', fontSize: 11, color: '#5b6875', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {r.assigneeName ?? <span style={{ color: '#bdc4cd' }}>—</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Modal>
  );
}
